<template>
    <div class="data-table">
        <!-- Every column hidden: there is no table to draw, so say why -->
        <div v-if="headers.length === 0" class="p-3 text-center text-muted">
            <slot name="no-columns">{{ noColumnsText }}</slot>
        </div>

        <!-- One scroll box for the whole table, both ways. The table inside is
             as wide as its columns need to be, so when they no longer fit it is
             this box that scrolls, and the head sticks to the top of it. -->
        <div v-else ref="viewport" class="data-table__viewport custom-scrollbar">
            <table
                class="data-table__table"
                :class="{ 'data-table__table--sticky-head': stickyHead }"
                :style="tableStyle"
            >
                <thead class="data-table__head">
                    <draggable
                        tag="tr"
                        class="data-table__row"
                        handle=".data-table__grip"
                        :value="headers"
                        :animation="200"
                        :disabled="!reorderable"
                        @input="emitOrder"
                    >
                        <th
                            v-for="header in headers"
                            :key="header.value"
                            scope="col"
                            class="data-table__cell"
                            :class="{ 'is-sorted': isSortedBy(header) }"
                        >
                            <!-- The heading is laid out inside its cell rather
                                 than by it: a `th` that stops being a table cell
                                 stops taking part in sizing its column. -->
                            <div class="data-table__cell-content">
                                <button
                                    v-if="header.sortable"
                                    type="button"
                                    class="data-table__sort"
                                    v-b-tooltip.hover
                                    :title="sortColumnText"
                                    @click.stop="toggleSort(header)"
                                >
                                    <i :class="sortIcon(header)"></i>
                                </button>

                                <span class="data-table__label">{{ header.text }}</span>

                                <!-- Kept together on the right, whether or not
                                     the column also carries a sort button on
                                     the left -->
                                <span
                                    v-if="reorderable || removable"
                                    class="data-table__cell-actions"
                                >
                                    <i
                                        v-if="reorderable"
                                        class="data-table__grip fa-solid fa-grip-vertical"
                                        v-b-tooltip.hover
                                        :title="moveColumnText"
                                        @click.stop
                                    ></i>

                                    <button
                                        v-if="removable"
                                        type="button"
                                        class="data-table__remove"
                                        v-b-tooltip.hover
                                        :title="removeColumnText"
                                        @click.stop="$emit('remove-column', header.value)"
                                    >
                                        <i class="fa-solid fa-xmark"></i>
                                    </button>
                                </span>
                            </div>
                        </th>
                    </draggable>
                </thead>

                <tbody class="data-table__body">
                    <tr v-if="sortedItems.length === 0">
                        <td class="data-table__message" :colspan="headers.length">
                            <slot name="no-data">{{ noDataText }}</slot>
                        </td>
                    </tr>

                    <!-- The rows above and below the ones rendered, as the
                         height they would have taken. The table is laid out over
                         its whole length whatever is in view, so scrolling is the
                         browser's own and the columns never notice. One empty
                         cell across every column: it must not be part of sizing
                         them. -->
                    <tr v-if="topPadding > 0" class="data-table__spacer" aria-hidden="true">
                        <td :colspan="headers.length" :style="{ height: `${topPadding}px` }"></td>
                    </tr>

                    <!-- `data-index` is how the virtualizer knows which row it
                         has just measured, and `v-measure-row` is what hands it
                         the element: Vue 2 has no function refs, so its usual
                         `:ref="measureElement"` is a directive here. -->
                    <tr
                        v-for="row in renderedRows"
                        :key="rowKey(row.item, row.index)"
                        v-measure-row="measureRow"
                        :data-index="row.index"
                        class="data-table__row"
                        :class="rowClass(row.item)"
                        @click="$emit('click:row', row.item)"
                    >
                        <td v-for="header in headers" :key="header.value" class="data-table__cell">
                            <!--
                                Per-column slot, named after the column, exactly
                                like `item.<key>` in Vuetify, dash-separated. Without one the raw
                                value is printed.
                            -->
                            <slot
                                :name="`item-${header.value}`"
                                :item="row.item"
                                :value="cellValue(row.item, header)"
                                :fallback="emptyFallbackValue"
                                :header="header"
                            >
                                {{ cellValue(row.item, header) }}
                            </slot>
                        </td>
                    </tr>

                    <tr v-if="bottomPadding > 0" class="data-table__spacer" aria-hidden="true">
                        <td
                            :colspan="headers.length"
                            :style="{ height: `${bottomPadding}px` }"
                        ></td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>
</template>

<script>
import get from 'lodash/get'
import draggable from 'vuedraggable'
import { computed, shallowRef } from 'vue'
import { useVirtualizer } from '@tanstack/vue-virtual'

// Rows kept rendered beyond each end of the viewport, so scrolling never
// reaches an empty patch before Vue has caught up.
const OVERSCAN_ROWS = 10

/**
 * Generic data table.
 *
 * Renders a `headers` description and a list of `items`, and knows nothing
 * about what it is displaying: every column can be given its own markup through
 * an `item-<column>` scoped slot, the way Vuetify's data table uses
 * `item.<column>`. The separator is a dash rather than a dot because
 * `vue/valid-v-slot` reads a dot as a slot modifier.
 *
 *   <data-table :headers="headers" :items="rows" item-key="id">
 *       <template #item-plate="{ item }">
 *           <b>{{ item.plate }}</b>
 *       </template>
 *   </data-table>
 *
 * A header is `{ text, value, sortable, sortKey }`, where `value` names the
 * column (and therefore its slot) and `sortKey` is an optional path to sort on
 * when it differs from `value`.
 *
 * It is a plain `<table>`: the head and the rows are one table, so the browser's
 * own automatic layout sizes each column to the widest thing in it, the heading
 * included, and no width is ever measured or handed around here. Nothing is
 * wrapped or cut — once the columns no longer fit, the table scrolls sideways.
 * The table has no size of its own either: it fills the box its parent gives it.
 *
 * `virtualized` keeps that one layout while taking the rows out of the DOM.
 * Only a window of them is rendered, with the rows above and below standing in
 * as two empty `<tr>` of exactly the height they would have taken, so the table
 * is its full length whatever is in view and the scrolling is the browser's own.
 * The rendered rows are ordinary rows in the flow of that table, which is what
 * separates this from a virtual scroller: a scroller positions its rows
 * absolutely, and an absolutely positioned row leaves the table layout behind,
 * taking the columns of the head with it.
 *
 * Which rows those are is `@tanstack/vue-virtual`'s answer, not ours: it owns
 * the scroll and resize listeners, the window, and the running measurement of
 * how tall the rows actually turned out (`rowHeight` is only the estimate it
 * starts from). All this reads back are the items to render and the two heights
 * to stand in for the rest.
 *
 * The one thing it costs: a column is sized by the rows that are rendered, so a
 * value longer than anything else in its column widens it as it scrolls in.
 */
export default {
    name: 'DataTable',
    components: {
        draggable
    },
    props: {
        headers: {
            type: Array,
            required: true
        },
        items: {
            type: Array,
            default: () => []
        },
        // Property used as the row key
        itemKey: {
            type: String,
            default: null
        },
        // How tall every row is, in pixels. A minimum rather than a cap: a cell
        // that needs more room than that gets it. While `virtualized` it is also
        // the size a row is assumed to have before it has been rendered, which
        // is corrected against the real thing as soon as it has been.
        rowHeight: {
            type: Number,
            default: 35
        },
        // The head stays on screen while the rows scroll under it
        stickyHead: {
            type: Boolean,
            default: false
        },
        // Render only the rows in view, the rest standing in as height
        virtualized: {
            type: Boolean,
            default: false
        },
        // Column currently sorted, as a header `value`. Supports `.sync`.
        sortBy: {
            type: String,
            default: null
        },
        sortDesc: {
            type: Boolean,
            default: false
        },
        // Replaces the built-in sorting: (items, sortKey, sortDesc) => items
        customSort: {
            type: Function,
            default: null
        },
        // Extra classes per row: a string, or (item) => string | object
        itemClass: {
            type: [String, Function],
            default: ''
        },
        noDataText: {
            type: String,
            default: '-'
        },
        // Shown in place of the table when `headers` is empty
        noColumnsText: {
            type: String,
            default: '-'
        },
        // Printed by `cellValue()` whenever a cell has no value to show
        emptyFallbackValue: {
            type: String,
            default: '--'
        },
        // Columns can be dragged into another order from their header, which
        // emits `reorder` with the header values in their new order
        reorderable: {
            type: Boolean,
            default: false
        },
        // Columns carry a button that emits `remove-column` with their value
        removable: {
            type: Boolean,
            default: false
        },
        sortColumnText: {
            type: String,
            default: ''
        },
        moveColumnText: {
            type: String,
            default: ''
        },
        removeColumnText: {
            type: String,
            default: ''
        }
    },
    /**
     * The virtualizer, over the scrolling viewport.
     *
     * `viewport` is filled in by the `ref` of the same name in the template —
     * Vue 2.7 syncs one into a ref returned from `setup()` — which is what lets
     * it be a reactive dependency: the element is behind a `v-if` (no columns,
     * no table), so it appears and disappears, and the virtualizer has to
     * follow it. Handed `null` while not `virtualized`, it attaches no
     * listeners at all.
     */
    setup(props) {
        const viewport = shallowRef(null)

        const virtualizer = useVirtualizer(
            computed(() => ({
                // Off `items` rather than the sorted list, which `setup` cannot
                // see: sorting reorders rows, it never adds or drops one.
                count: props.virtualized ? props.items.length : 0,
                getScrollElement: () => (props.virtualized ? viewport.value : null),
                estimateSize: () => props.rowHeight,
                overscan: OVERSCAN_ROWS
            }))
        )

        return { viewport, virtualizer }
    },
    directives: {
        // Vue 2 has no function refs, so this is how each row hands itself to
        // `measureElement`, on the way in and on every re-render. `null` on the
        // way out lets it drop the rows that have left the DOM.
        measureRow: {
            inserted(el, binding) {
                binding.value(el)
            },
            componentUpdated(el, binding) {
                binding.value(el)
            },
            unbind(el, binding) {
                binding.value(null)
            }
        }
    },
    computed: {
        keyField() {
            return this.itemKey || 'id'
        },
        tableStyle() {
            return { '--data-table-row-height': `${this.rowHeight}px` }
        },
        sortedItems() {
            const header = this.headers.find((h) => h.value === this.sortBy)

            if (this.customSort) {
                return this.customSort(this.items, this.sortKeyOf(header), this.sortDesc)
            }

            if (!header) return this.items

            const sortKey = this.sortKeyOf(header)
            const direction = this.sortDesc ? -1 : 1

            // Copy first: sort() mutates, and `items` belongs to the caller
            return [...this.items].sort((a, b) => {
                const aValue = get(a, sortKey)
                const bValue = get(b, sortKey)

                if (aValue === bValue) return 0
                if (aValue === null || aValue === undefined) return 1
                if (bValue === null || bValue === undefined) return -1

                return aValue < bValue ? -direction : direction
            })
        },
        /**
         * The window, as the virtualizer sees it: each entry carries the index
         * of the row and where it sits, which is where the spacers come from.
         * Empty unless `virtualized` — the count is zero then, so there is
         * nothing to hand out.
         */
        virtualRows() {
            return this.virtualizer.getVirtualItems()
        },
        /**
         * What the `v-for` walks, as `{ item, index }`: the index is the row's
         * place in the whole list, not in the window, which both its key and
         * its measurement are read from.
         */
        renderedRows() {
            if (!this.virtualized) {
                return this.sortedItems.map((item, index) => ({ item, index }))
            }

            // A list that has just got shorter can leave the virtualizer a
            // tick behind, holding indexes that are now past the end of it
            return this.virtualRows
                .map((row) => ({ item: this.sortedItems[row.index], index: row.index }))
                .filter((row) => row.item !== undefined)
        },
        /** The rows before the window, as the height they take up. */
        topPadding() {
            return this.virtualRows.length ? this.virtualRows[0].start : 0
        },
        /** And the rows after it. */
        bottomPadding() {
            if (!this.virtualRows.length) return 0

            const lastRow = this.virtualRows[this.virtualRows.length - 1]

            return this.virtualizer.getTotalSize() - lastRow.end
        }
    },
    methods: {
        // `draggable` hands back the reordered headers; the owner of the
        // columns decides what to do about it.
        emitOrder(headers) {
            this.$emit(
                'reorder',
                headers.map((header) => header.value)
            )
        },
        // Hands one row to the virtualizer to be measured, or `null` for a row
        // on its way out. Nothing to measure when every row is rendered.
        measureRow(el) {
            if (!this.virtualized) return

            this.virtualizer.measureElement(el)
        },
        // The row key, falling back to its position for a list that has none
        rowKey(item, index) {
            const key = get(item, this.keyField)

            return key === null || key === undefined ? index : key
        },
        sortKeyOf(header) {
            if (!header) return null

            return header.sortKey || header.value
        },
        /**
         * Value shown in a cell: the header path read off the row, or
         * `emptyFallbackValue` when there is nothing to show. Sorting reads
         * the raw value instead, so the fallback never takes part in it.
         */
        cellValue(item, header) {
            const value = get(item, this.sortKeyOf(header))

            if (value === null || value === undefined || value === '') {
                return this.emptyFallbackValue
            }

            return value
        },
        rowClass(item) {
            return typeof this.itemClass === 'function' ? this.itemClass(item) : this.itemClass
        },
        isSortedBy(header) {
            return this.sortBy === header.value
        },
        sortIcon(header) {
            if (!this.isSortedBy(header)) return 'fa-solid fa-sort'

            return this.sortDesc ? 'fa-solid fa-sort-down' : 'fa-solid fa-sort-up'
        },
        /**
         * Cycles through ascending, descending and unsorted, so a third click
         * puts the table back into its natural order.
         */
        toggleSort(header) {
            if (!header.sortable) return

            if (!this.isSortedBy(header)) {
                this.$emit('update:sortBy', header.value)
                this.$emit('update:sortDesc', false)
                return
            }

            if (!this.sortDesc) {
                this.$emit('update:sortDesc', true)
                return
            }

            this.$emit('update:sortBy', null)
            this.$emit('update:sortDesc', false)
        }
    }
}
</script>

<style scoped lang="scss">
// No width and no height of its own: the table fills whatever the panel around
// it gives, and everything inside sizes against that.
.data-table {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    min-height: 0;
    min-width: 0;
}

// The scroll container, both ways
.data-table__viewport {
    flex: 1 1 auto;
    min-height: 0;
    overflow: auto;
}

// Automatic layout is the whole reason this is a real table: every column comes
// out as wide as the widest thing in it, the heading included, and nothing had
// to be measured to get there. `width: 100%` only shares out room the columns
// do not need — a table is never laid out narrower than its content.
//
// Borders are separate rather than collapsed: a collapsed border belongs to the
// table rather than to the cell, and stays behind when a sticky heading moves.
.data-table__table {
    width: 100%;
    table-layout: auto;
    border-collapse: separate;
    border-spacing: 0;
}

.data-table__row {
    height: var(--data-table-row-height);
}

// Stands in for the rows that are not rendered. Empty and borderless: the
// height is the whole of it, and a cell spanning every column must not have a
// say in how wide any of them is.
.data-table__spacer td {
    padding: 0;
    border: 0;
}

.data-table__cell {
    padding: 5px;
    text-align: left;
    vertical-align: middle;
    font-size: 13px;
    border-bottom: 1px solid var(--main-gray);
    border-right: 1px solid var(--main-gray);

    // Never wrapped and never cut: a value longer than the room there is widens
    // its column instead, and the table scrolls sideways when it runs out.
    white-space: nowrap;
}

.data-table__head {
    .data-table__cell {
        background: var(--main-inner-bg-color);
        font-size: 14px;
        font-weight: 600;
    }

    .data-table__cell-content {
        display: flex;
        align-items: center;
        gap: 4px;
    }

    // Flex items shrink by default: the sort icon, the grip and the remove
    // button would be squashed to nothing in a narrow column instead of the
    // column being made wide enough for all three.
    .data-table__cell-content > * {
        flex: 0 0 auto;
    }

    // A heading is the name of the column: it is shown whole, and the column is
    // wide enough to hold it.
    .data-table__label {
        white-space: nowrap;
    }

    // Kept in the layout even when out of sight, so a column does not change
    // shape under the pointer.
    .data-table__cell-actions {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        margin-left: auto;
        padding-left: 6px;
        visibility: hidden;
        color: var(--main-gray--darker);
    }

    .data-table__cell:hover .data-table__cell-actions {
        visibility: visible;
    }

    .data-table__grip {
        cursor: grab;
    }

    .data-table__remove {
        border: 0;
        background: transparent;
        padding: 0;
        line-height: 1;

        &:hover {
            color: var(--danger, #dc3545);
        }
    }

    // On the left of the heading, where it reads as "sorted by this". Clicking
    // it is the only thing that sorts: the rest of the heading is for reading,
    // dragging and hiding.
    .data-table__sort {
        border: 0;
        background: transparent;
        padding: 0;
        margin-right: 2px;
        line-height: 1;
        color: inherit;
        opacity: 0.5;
        cursor: pointer;

        &:hover {
            opacity: 1;
        }
    }

    .is-sorted {
        background: var(--main-gray--darker);

        .data-table__sort {
            opacity: 1;
        }
    }
}

// Stuck cell by cell rather than by row group: a `thead` only sticks in some
// browsers, a `th` sticks in all of them.
.data-table__table--sticky-head .data-table__head .data-table__cell {
    position: sticky;
    top: 0;
    z-index: 1;
}

.data-table__body .data-table__row:hover .data-table__cell {
    background: var(--main-focus);
    border-color: var(--main-focus);
}

.data-table__message {
    padding: 10px;
    text-align: center;
    color: var(--main-gray--darker);
}
</style>
